import { PureComponent, createRef, Fragment } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { FiSearch, FiColumns } from 'react-icons/fi'
import i18n from '../../i18n'
import { Api } from '../../Services'
import swaller from '../../Library/Tools/Swaller'
import { SEZIONE_ICONS } from '../../Utils/sezioniDisplay'
import { formatHit, queryValida, SEZIONE_PER_TIPO } from '../../Utils/searchFormat'
import { apriRisultato } from '../../Utils/searchNavigation'

const t = (key, opts) => i18n.t(key, { ns: 'navbar', ...opts })

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1050; /* sopra modali pagina (~1000), sotto Swaller (1099/1100) */
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 12vh;
  background: ${({ theme }) => theme.colors.shadow.overlay};

  @media (max-width: 768px) { padding-top: 0; }
`

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  width: min(640px, 100%);
  max-height: 70vh;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.medium};
  border-radius: ${({ theme }) => theme.radius.card};
  box-shadow: 0 12px 40px ${({ theme }) => theme.colors.shadow.strong};
  overflow: hidden;

  @media (max-width: 768px) { width: 100%; height: 100%; max-height: none; border-radius: 0; border: 0; }
`

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  color: ${({ theme }) => theme.colors.text.muted};
`

const Input = styled.input`
  flex: 1;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text.primary};
`

const Results = styled.div`
  overflow-y: auto;
  padding: 0.5rem 0;
`

const Msg = styled.div`
  padding: 1rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const Group = styled.div`
  padding: 0.5rem 1rem 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.muted};
`

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.625rem 1rem;
  border: 0;
  text-align: left;
  cursor: pointer;
  background: ${({ $active, theme }) => ($active ? theme.colors.semantic.primary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.text.inverse : theme.colors.text.primary)};

  small { display: block; opacity: 0.75; }
`

const groupLabel = tipo => {
  if (tipo === 'board') return t('board')
  const sezione = SEZIONE_PER_TIPO[tipo]
  return i18n.t(`titles.${sezione}`, { ns: 'home', defaultValue: sezione })
}

const groupIcon = tipo => (tipo === 'board' ? FiColumns : SEZIONE_ICONS[SEZIONE_PER_TIPO[tipo]] || FiSearch)

// Palette Cmd/Ctrl+K. Listener in capture su window: Esc chiude solo la palette (drawer e bottom sheet
// ascoltano Esc su document) e Ctrl+K non apre la ricerca del browser.
class GlobalSearch extends PureComponent {
  state = { open: false, query: '', hits: [], loading: false, error: false, active: 0 }
  inputRef = createRef()
  _seq = 0 // scarta le risposte di ricerche superate
  _timer = null
  _busy = false

  componentDidMount () { window.addEventListener('keydown', this.onKey, true) }

  componentWillUnmount () {
    window.removeEventListener('keydown', this.onKey, true)
    clearTimeout(this._timer)
    this._seq++
  }

  onKey = e => {
    if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key || '').toLowerCase() === 'k') {
      e.preventDefault()
      if (this.state.open) this.close()
      else this.open()
      return
    }
    if (e.key === 'Escape' && this.state.open) {
      if (document.querySelector('[data-swaller-modal]')) return // Esc chiude l'errore Swaller, non la palette
      e.stopImmediatePropagation()
      this.close()
    }
  }

  open = () => {
    if (document.querySelector('[data-swaller-modal]')) return
    this.setState({ open: true }, () => this.inputRef.current && this.inputRef.current.focus())
  }

  close = () => {
    clearTimeout(this._timer)
    this._seq++
    this.setState({ open: false, query: '', hits: [], loading: false, error: false, active: 0 })
  }

  onChange = e => {
    const query = e.target.value
    clearTimeout(this._timer)
    const seq = ++this._seq
    if (!queryValida(query)) {
      this.setState({ query, hits: [], loading: false, error: false, active: 0 })
      return
    }
    this.setState({ query, loading: true, error: false, active: 0 })
    this._timer = setTimeout(() => this.cerca(query, seq), 250)
  }

  cerca = async (query, seq) => {
    const res = await Api.get('/search', { q: query.trim() })
    if (seq !== this._seq) return
    if (!res.ok) return this.setState({ loading: false, error: true, hits: [] })
    this.setState({ loading: false, hits: (res.data && res.data.data) || [] })
  }

  onInputKey = e => {
    const { hits, active } = this.state
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      this.setState({ active: Math.min(active + 1, Math.max(hits.length - 1, 0)) })
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      this.setState({ active: Math.max(active - 1, 0) })
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (!this.state.loading) this.seleziona(hits[active])
    }
  }

  seleziona = async hit => {
    if (!hit || this._busy) return
    this._busy = true
    try {
      const ok = await apriRisultato(hit, { navigate: this.props.navigate, Api })
      if (ok) this.close()
      else swaller.error(t('searchOpenError'))
    } finally {
      this._busy = false
    }
  }

  render () {
    const { open, query, hits, loading, error, active } = this.state
    if (!open) return null
    const valida = queryValida(query)
    let prev = null
    return createPortal(
      <Overlay onMouseDown={e => { if (e.target === e.currentTarget) this.close() }}>
        <Panel role='dialog' aria-modal='true' aria-label={t('search')}>
          <InputRow>
            <FiSearch />
            <Input
              ref={this.inputRef}
              value={query}
              onChange={this.onChange}
              onKeyDown={this.onInputKey}
              placeholder={t('searchPlaceholder')}
              aria-label={t('search')}
            />
          </InputRow>
          <Results>
            {!valida && <Msg>{t('searchHint')}</Msg>}
            {valida && error && <Msg>{t('searchError')}</Msg>}
            {valida && !error && !loading && hits.length === 0 && <Msg>{t('searchEmpty')}</Msg>}
            {hits.map((hit, i) => {
              const { titolo, sottotitolo } = formatHit(hit)
              const Icon = groupIcon(hit.tipo)
              const header = hit.tipo !== prev ? <Group>{groupLabel(hit.tipo)}</Group> : null
              prev = hit.tipo
              return (
                <Fragment key={`${hit.tipo}-${hit._id}`}>
                  {header}
                  <Row $active={i === active} onMouseEnter={() => this.setState({ active: i })} onClick={() => this.seleziona(hit)}>
                    <Icon size={18} />
                    <span>{titolo}{sottotitolo && <small>{sottotitolo}</small>}</span>
                  </Row>
                </Fragment>
              )
            })}
          </Results>
        </Panel>
      </Overlay>,
      document.body
    )
  }
}

export default GlobalSearch
